import React, { useEffect, useMemo, useState } from "react";
import { Subtitle } from "../styles";
import FriendItem from "./FriendItem";
import { Container, CustomButton } from "./styles";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useLinks } from "hooks/useLinks";
import { friendShopAPI } from "redux/apis/friendShop";
import { FriendSelectionSkeleton } from "./skeleton";
import Pagination from "components/Pagination";
import { useGoogleAnalyticsContext } from "contexts";

const FRIENDS_PER_PAGE = 6;

const FriendSelection = () => {
	const { giftIndex } = useParams();
	const navigate = useNavigate();
	const { friend_center } = useLinks();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const [currentPage, setCurrentPage] = useState(1);
	const {
		data: giftsData,
		status: giftsStatus,
		isLoading: giftsIsLoading
	} = friendShopAPI.endpoints.getGiftsFriendShop.useQueryState(undefined);
	const { data: friendsData, isLoading: friendsIsLoading } =
		friendShopAPI.endpoints.getFriendsFriendShop.useQueryState(undefined);
	const parsedGiftIndex = Number(giftIndex);
	const selectedGift = Number.isInteger(parsedGiftIndex)
		? giftsData?.shopOffers[parsedGiftIndex]
		: undefined;
	const friends = friendsData?.users ?? [];
	const totalPages = Math.ceil(friends.length / FRIENDS_PER_PAGE);
	const paginatedFriends = useMemo(() => {
		const start = (currentPage - 1) * FRIENDS_PER_PAGE;
		return friends.slice(start, start + FRIENDS_PER_PAGE);
	}, [friends, currentPage]);

	const querySettled =
		giftsStatus === "fulfilled" || giftsStatus === "rejected";

	const handleBack = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "friend_list_click",
			event_type: "friend_list",
			action: "back_button_click",
			source: "friend_shop"
		});
		navigate(`${friend_center.path}/shop/gifts`);
	};

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
	}, []);

	useEffect(() => {
		setCurrentPage(1);
	}, [parsedGiftIndex]);

	useEffect(() => {
		if (totalPages > 0 && currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		if (giftsIsLoading || friendsIsLoading) {
			return;
		}
		if (!friendsData?.success || !giftsData?.success) {
			return;
		}

		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "friend_list_shown",
			event_type: "friend_list",
			action: "show",
			source: "friend_shop"
		});
	}, [
		friendsData?.success,
		giftsData?.success,
		friendsIsLoading,
		giftsIsLoading
	]);

	if (querySettled && !selectedGift) {
		return <Navigate to={`${friend_center.path}/shop/gifts`} replace />;
	}

	if (giftsIsLoading || friendsIsLoading) {
		return (
			<>
				<Subtitle>
					Choose the FRIEND who will receive a{" "}
					{selectedGift?.bannerText || "gift"}
				</Subtitle>
				<FriendSelectionSkeleton />
			</>
		);
	}

	return (
		<>
			<Subtitle>
				Choose the FRIEND who will receive a{" "}
				{selectedGift?.bannerText || "gift"}
			</Subtitle>
			<Container>
				{paginatedFriends.map((friend) => {
					return (
						<FriendItem
							key={friend.userId}
							data={friend}
							selectedGift={selectedGift}
						/>
					);
				})}
			</Container>
			<Pagination
				totalItems={friends.length}
				pageSize={FRIENDS_PER_PAGE}
				page={currentPage}
				onPageChange={setCurrentPage}
			/>
			<CustomButton themeColor="secondary" onClick={handleBack}>
				Back
			</CustomButton>
		</>
	);
};

export default FriendSelection;
